import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL;
const API = `${BASE}/api`;

const client = axios.create({ baseURL: API });

client.interceptors.request.use((config) => {
  const token = localStorage.getItem("agp_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

client.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response && err.response.status === 401) {
      localStorage.removeItem("agp_token");
      if (!window.location.pathname.startsWith("/login")) {
        window.location.href = "/login";
      }
    }
    return Promise.reject(err);
  }
);

export const api = {
  plannedItems: () => client.get('/planned/items').then(r => r.data),
  plannedScans: () => client.get('/planned/scans').then(r => r.data),
  plannedFeedback: () => client.get('/planned/feedback').then(r => r.data),
  ingestPlanned: (id, body) => client.put(`/planned/items/${encodeURIComponent(id)}`, body).then(r => r.data),
  reviewPlanned: (item, body) => client.post(`/planned/items/${item.kind}/${encodeURIComponent(item.id)}/review`, { revision: item.revision, ...body }).then(r => r.data),
  // auth
  login: (email, password) => client.post("/auth/login", { email, password }).then((r) => r.data),
  me: () => client.get("/auth/me").then((r) => r.data),
  // crm
  people: () => client.get("/people").then((r) => r.data),
  person: (id) => client.get(`/people/${id}`).then((r) => r.data),
  createPerson: (b) => client.post("/people", b).then((r) => r.data),
  updatePerson: (id, b) => client.patch(`/people/${id}`, b).then((r) => r.data),
  deletePerson: (id) => client.delete(`/people/${id}`).then((r) => r.data),
  companies: () => client.get("/companies").then((r) => r.data),
  company: (id) => client.get(`/companies/${id}`).then((r) => r.data),
  createCompany: (b) => client.post("/companies", b).then((r) => r.data),
  updateCompany: (id, b) => client.patch(`/companies/${id}`, b).then((r) => r.data),
  deleteCompany: (id) => client.delete(`/companies/${id}`).then((r) => r.data),
  deals: () => client.get("/deals").then((r) => r.data),
  createDeal: (b) => client.post("/deals", b).then((r) => r.data),
  updateDeal: (id, b) => client.patch(`/deals/${id}`, b).then((r) => r.data),
  deleteDeal: (id) => client.delete(`/deals/${id}`).then((r) => r.data),
  // sequences
  sequences: () => client.get("/sequences").then((r) => r.data),
  sequence: (id) => client.get(`/sequences/${id}`).then((r) => r.data),
  createSequence: (b) => client.post("/sequences", b).then((r) => r.data),
  updateSequence: (id, b) => client.patch(`/sequences/${id}`, b).then((r) => r.data),
  deleteSequence: (id) => client.delete(`/sequences/${id}`).then((r) => r.data),
  activateSequence: (id, active) => client.post(`/sequences/${id}/activate`, { active }).then((r) => r.data),
  enroll: (id, person_id) => client.post(`/sequences/${id}/enroll`, { person_id }).then((r) => r.data),
  enrollments: () => client.get("/enrollments").then((r) => r.data),
  enrollment: (id) => client.get(`/enrollments/${id}`).then((r) => r.data),
  editStep: (eid, sid, b) => client.patch(`/enrollments/${eid}/steps/${sid}`, b).then((r) => r.data),
  approveStep: (eid, sid) => client.post(`/enrollments/${eid}/steps/${sid}/approve`).then((r) => r.data),
  sendStep: (eid, sid) => client.post(`/enrollments/${eid}/steps/${sid}/send`).then((r) => r.data),
  regenerate: (eid) => client.post(`/enrollments/${eid}/regenerate`).then((r) => r.data),
  markReplied: (eid) => client.post(`/enrollments/${eid}/reply`).then((r) => r.data),
  // content
  posts: () => client.get("/content/posts").then((r) => r.data),
  createPost: (b) => client.post("/content/posts", b).then((r) => r.data),
  draftPost: (b) => client.post("/content/draft", b).then((r) => r.data),
  updatePost: (id, b) => client.patch(`/content/posts/${id}`, b).then((r) => r.data),
  deletePost: (id) => client.delete(`/content/posts/${id}`).then((r) => r.data),
  approvePost: (id) => client.post(`/content/posts/${id}/approve`).then((r) => r.data),
  publishPost: (id) => client.post(`/content/posts/${id}/publish`).then((r) => r.data),
  research: () => client.get("/content/research").then((r) => r.data),
  // social listening
  socialProfiles: () => client.get("/social/profiles").then((r) => r.data),
  addSocialProfile: (b) => client.post("/social/profiles", b).then((r) => r.data),
  updateSocialProfile: (id, b) => client.patch(`/social/profiles/${id}`, b).then((r) => r.data),
  removeSocialProfile: (id) => client.delete(`/social/profiles/${id}`).then((r) => r.data),
  socialPosts: () => client.get("/social/posts").then((r) => r.data),
  addSocialPost: (b) => client.post("/social/posts", b).then((r) => r.data),
  updateSocialPost: (id, b) => client.patch(`/social/posts/${id}`, b).then((r) => r.data),
  deleteSocialPost: (id) => client.delete(`/social/posts/${id}`).then((r) => r.data),
  approveSocialResponse: (id) => client.post(`/social/posts/${id}/approve-response`).then((r) => r.data),
  markSocialPosted: (id) => client.post(`/social/posts/${id}/mark-posted`).then((r) => r.data),
  // tasks + misc
  tasks: () => client.get("/tasks").then((r) => r.data),
  createTask: (b) => client.post("/tasks", b).then((r) => r.data),
  updateTask: (id, b) => client.patch(`/tasks/${id}`, b).then((r) => r.data),
  deleteTask: (id) => client.delete(`/tasks/${id}`).then((r) => r.data),
  activity: () => client.get("/activity").then((r) => r.data),
  stats: () => client.get("/stats").then((r) => r.data),
  // Hermes cron control plane
  cronJobs: () => client.get("/cron/jobs").then((r) => r.data),
  cronRuns: (jobId, limit = 20) => client.get("/cron/runs", { params: { job_id: jobId || undefined, limit } }).then((r) => r.data),
  cronStatus: () => client.get("/cron/status").then((r) => r.data),
  pauseCronJob: (id) => client.post(`/cron/jobs/${id}/pause`).then((r) => r.data),
  resumeCronJob: (id) => client.post(`/cron/jobs/${id}/resume`).then((r) => r.data),
  runCronJob: (id) => client.post(`/cron/jobs/${id}/run`).then((r) => r.data),
  editCronJob: (id, body) => client.post(`/cron/jobs/${id}/edit`, body).then((r) => r.data),
  // integrations
  integrationSettings: () => client.get("/integrations/settings").then((r) => r.data),
  saveIntegrationSettings: (b) => client.put("/integrations/settings", b).then((r) => r.data),
  testResend: (to) => client.post("/integrations/resend/test", { to }).then((r) => r.data),
  composioTools: () => client.get("/integrations/composio/tools").then((r) => r.data),
  // mailbox
  mailboxThreads: (scope = "all") => client.get("/mailbox/threads", { params: { scope } }).then((r) => r.data),
  mailboxThread: (id) => client.get(`/mailbox/threads/${id}`).then((r) => r.data),
  mailboxMessageAttachments: (id) => client.get(`/mailbox/messages/${id}/attachments`).then((r) => r.data),
  markMailboxThreadRead: (id) => client.post(`/mailbox/threads/${id}/read`).then((r) => r.data),
  replyMailbox: (id, text, idempotencyKey, options = {}) => client.post(`/mailbox/threads/${id}/reply`, { text, ...options }, { headers: idempotencyKey ? { "Idempotency-Key": idempotencyKey } : {} }).then((r) => r.data),
  composeMailbox: (personId, subject, text, idempotencyKey, options = {}) => client.post("/mailbox/compose", {
    person_id: personId,
    subject,
    text,
    ...options,
  }, { headers: idempotencyKey ? { "Idempotency-Key": idempotencyKey } : {} }).then((r) => r.data),
};

export default api;
